import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1680992044138-ce4864c2b962"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/85" />
        <div className="relative h-full flex flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-white/15 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <span className="font-heading font-black tracking-tight">ITASSESS</span>
          </div>
          <div>
            <h1 className="font-heading font-black text-4xl tracking-tighter leading-tight">
              Valuta, confronta e ottimizza il tuo stack IT.
            </h1>
            <p className="mt-4 text-base text-primary-foreground/80 max-w-md">
              Assessment strutturato dei tool IT con punteggi ponderati, confronti e analisi AI dei processi aziendali.
            </p>
          </div>
          <div className="data-label text-primary-foreground/60">Piattaforma interna per team IT</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <h2 className="font-heading font-black text-2xl tracking-tight">
            Accedi
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Entra nella tua area di assessment
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="data-label">Email</Label>
              <Input id="email" type="email" data-testid="email-input" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 rounded-none" placeholder="nome@azienda.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="data-label">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-primary hover:underline">Password dimenticata?</Link>
              </div>
              <Input id="password" type="password" data-testid="password-input" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 rounded-none" placeholder="••••••••" />
            </div>

            {error && <div data-testid="auth-error" className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2">{error}</div>}

            <Button type="submit" data-testid="submit-auth-btn" disabled={loading} className="w-full rounded-none font-medium">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Accedi
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="data-label">oppure</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <Button variant="outline" data-testid="google-login-btn" onClick={googleLogin} className="w-full rounded-none font-medium">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4 mr-2" />
            Continua con Google
          </Button>
        </div>
      </div>
    </div>
  );
}
